import * as React from "react";
import { Check, ShieldAlert } from "lucide-react";

import { cn } from "@/lib/utils";
import { usePortal } from "@/features/portal/portal-context";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function TopBar() {
  const { activeBrand, viewingOthersBrand, dirty, saving, lastSaved, save, discard } = usePortal();
  // When publishing to a brand that isn't yours, confirm first so it's never an
  // accidental write to someone else's live app.
  const [confirming, setConfirming] = React.useState(false);

  function onSaveClick() {
    if (viewingOthersBrand) {
      setConfirming(true);
      return;
    }
    void save();
  }

  function confirmSave() {
    setConfirming(false);
    void save();
  }

  return (
    <div
      className="flex items-center justify-between border-b border-border px-9 py-3.5 backdrop-blur-[10px]"
      style={{ background: "color-mix(in oklab, var(--background) 82%, transparent)" }}
    >
      <div className="flex items-center gap-2.5 text-[13px] text-text-3">
        <span className="font-semibold text-muted-foreground">{activeBrand?.name}</span>
        {viewingOthersBrand && activeBrand?.ownerName && (
          <span className="text-text-3">· owned by {activeBrand.ownerName}</span>
        )}
        <span>/</span>
        <span className={cn("font-semibold", dirty ? "text-[#f5b945]" : "text-text-3")}>
          {dirty ? "Unsaved changes" : lastSaved ? "All changes saved" : "No changes yet"}
        </span>
      </div>
      <div className="flex items-center gap-2.5">
        {dirty && (
          <Button variant="ghost" size="sm" onClick={discard}>
            Discard
          </Button>
        )}
        <Button
          size="sm"
          onClick={onSaveClick}
          disabled={!dirty || saving}
          className={cn((!dirty || saving) && "opacity-50 shadow-none")}
        >
          {!saving && <Check className="size-4" />}
          {saving ? "Publishing…" : "Save & publish"}
        </Button>
      </div>

      {/* Extra guard rail: publishing to a brand you don't own. */}
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <div className="mb-1 grid size-11 place-items-center rounded-xl bg-[rgba(245,185,69,.16)] text-[#b9851f] dark:text-[#f5b945]">
              <ShieldAlert className="size-6" />
            </div>
            <DialogTitle>Publish to someone else’s brand?</DialogTitle>
            <DialogDescription>
              <b className="font-semibold text-foreground">{activeBrand?.name}</b>
              {activeBrand?.ownerName ? <> is owned by {activeBrand.ownerName} and</> : null} isn’t one
              of your own brands. As a super admin your changes will go live for them and be
              attributed to you.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button onClick={confirmSave}>
              <Check className="size-4" />
              Publish to {activeBrand?.name}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
